import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { calculatePrice } from '../utils/priceCalculator';
import api from '../utils/api';
import { trackAddToCart, trackRemoveFromCart, trackViewCart } from '../utils/analytics';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      return [];
    }
  });
  
  const [metalRates, setMetalRates] = useState([]);
  const isInitialized = useRef(false);
  const location = useLocation();
  const lastRatesFetch = useRef(0);

  // Fetch metal rates
  const fetchRates = useCallback(async (force = false) => {
    // Throttle: don't fetch more than once every 5 seconds unless forced
    const now = Date.now();
    if (!force && now - lastRatesFetch.current < 5000) return;
    
    try {
      const res = await api.get('/metal-rates');
      setMetalRates(res.data);
      lastRatesFetch.current = now;
    } catch (error) {
    }
  }, []);

  // Fetch rates on mount
  useEffect(() => {
    fetchRates(true);
  }, [fetchRates]);

  // Refresh rates on route change (page navigation)
  useEffect(() => {
    if (isInitialized.current) {
      fetchRates();
    }
  }, [location.pathname, fetchRates]);

  // Refresh rates when user returns to the tab/window
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRates();
      }
    };

    const handleFocus = () => {
      fetchRates();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchRates]);

  // Recalculate cart prices when metal rates change
  const recalculateCartPrices = useCallback(() => {
    if (metalRates.length === 0 || cart.length === 0) return;
    
    const updatedCart = cart.map(item => {
      const newPrice = calculatePrice(item.product, metalRates);
      if (newPrice) {
        return { ...item, price: newPrice };
      }
      return item;
    });
    
    // Only update if prices actually changed
    const hasChanges = updatedCart.some((item, index) => 
      item.price?.totalPrice !== cart[index].price?.totalPrice
    );
    
    if (hasChanges) {
      setCart(updatedCart);
    }
  }, [metalRates, cart]);

  // Recalculate prices when rates change
  useEffect(() => {
    if (isInitialized.current && metalRates.length > 0) {
      recalculateCartPrices();
    }
  }, [metalRates, recalculateCartPrices]);

  // Save cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialized.current) {
      localStorage.setItem('cart', JSON.stringify(cart));
    } else {
      isInitialized.current = true;
    }
  }, [cart]);

  // Function to manually refresh rates and recalculate prices
  const refreshCartPrices = async () => {
    await fetchRates(true);
  };

  const addToCart = (product, price) => {
    // Prevent adding sold out products
    if (product.is_sold_out) {
      return false;
    }
    
    const existingItem = cart.find(item => item.product.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { product, quantity: 1, price }]);
    }
    
    // Track add to cart event
    trackAddToCart({ ...product, total_price: price?.totalPrice }, 1);
    
    return true;
  };

  const removeFromCart = (productId) => {
    const item = cart.find(item => item.product.id === productId);
    if (item) {
      // Track remove from cart event
      trackRemoveFromCart({ ...item.product, total_price: item.price?.totalPrice }, item.quantity);
    }
    setCart(cart.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (parseFloat(item.price.totalPrice) * item.quantity), 0);
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotal, refreshCartPrices, metalRates }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);